import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, PencilSimple, Trash, Stack } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { api } from "@/lib/api";

const LABELS = { customers: "Customers", slabs: "Prices" };

export default function SavedImportLayouts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/import/mappings");
      setRows(data || []);
    } catch {
      toast.error("Could not load saved layouts");
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const saveName = async (r) => {
    try {
      await api.post("/import/mappings/rename", { key: r.key, name: draft });
      setEditing(null);
      toast.success("Layout renamed");
      load();
    } catch {
      toast.error("Could not rename");
    }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete the saved layout "${r.name}"?`)) return;
    try {
      await api.post("/import/mappings/delete", { key: r.key });
      toast.success("Layout deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Settings / Saved Import Layouts" />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/settings"
          data-testid="back-to-settings"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Settings
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
          Saved import layouts
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Column layouts remembered from your suppliers' files. When a file
          arrives with the same columns, it maps itself.
        </p>

        <div className="mt-6 space-y-3" data-testid="saved-layouts-list">
          {loading && <div className="text-sm text-zinc-500">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div
              data-testid="saved-layouts-empty"
              className="rounded-lg border border-dashed border-zinc-800 p-10 text-center text-sm text-zinc-500"
            >
              No saved layouts yet. On an import, adjust the column matches and
              tap “Save this layout”.
            </div>
          )}
          {rows.map((r) => (
            <div
              key={r.key}
              data-testid={`saved-layout-${r.key}`}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <Stack size={18} weight="bold" className="text-indigo-300" />
              <div className="min-w-0 flex-1">
                {editing === r.key ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      data-testid="rename-input"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveName(r);
                        if (e.key === "Escape") setEditing(null);
                      }}
                      className="h-9 w-64 rounded-md border border-zinc-700 bg-zinc-950 px-2 text-sm text-zinc-100 outline-none focus:border-indigo-500"
                    />
                    <Button
                      data-testid="rename-save"
                      onClick={() => saveName(r)}
                      className="h-9 bg-indigo-600 text-xs hover:bg-indigo-500"
                    >
                      <Check size={14} weight="bold" className="mr-1" /> Save
                    </Button>
                  </div>
                ) : (
                  <div className="truncate text-sm font-semibold text-zinc-100">
                    {r.name}
                  </div>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500">
                  <span
                    className={`rounded-full px-2 py-0.5 font-bold ${
                      r.entity === "slabs"
                        ? "bg-amber-500/15 text-amber-300"
                        : "bg-emerald-500/15 text-emerald-300"
                    }`}
                  >
                    {LABELS[r.entity] || r.entity}
                  </span>
                  <span>{r.columns} columns</span>
                  {r.updated_at && (
                    <span>· {new Date(r.updated_at).toLocaleDateString()}</span>
                  )}
                </div>
                <div className="mt-1 truncate font-mono-data text-[11px] text-zinc-600">
                  {(r.headers || []).join(", ")}
                </div>
              </div>
              {editing !== r.key && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    data-testid={`rename-${r.key}`}
                    onClick={() => {
                      setEditing(r.key);
                      setDraft(r.name);
                    }}
                    title="Rename"
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-700 text-zinc-400 hover:border-indigo-400 hover:text-indigo-200"
                  >
                    <PencilSimple size={15} weight="bold" />
                  </button>
                  <button
                    type="button"
                    data-testid={`delete-${r.key}`}
                    onClick={() => remove(r)}
                    title="Delete"
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-700 text-zinc-400 hover:border-rose-500/50 hover:text-rose-300"
                  >
                    <Trash size={15} weight="bold" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
